/**
 * Copyright (c) 2018-2025 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Representation } from '../../mol-repr/representation.js';
import { ModifiersKeys, ButtonsType } from '../../mol-util/input/input-observer.js';
import { RxEventHelper } from '../../mol-util/rx-event-helper.js';
import { Vec2, Vec3 } from '../../mol-math/linear-algebra.js';
import { ParamDefinition as PD } from '../../mol-util/param-definition.js';
import { Bond } from '../../mol-model/structure.js';
import { Ray3D } from '../../mol-math/geometry/primitives/ray3d.js';
const tmpPosA = Vec3();
const tmpPos = Vec3();
const tmpNorm = Vec3();
export const Canvas3dInteractionHelperParams = {
    maxFps: PD.Numeric(30, { min: 10, max: 60, step: 10 }),
    preferAtomPixelPadding: PD.Numeric(3, { min: 0, max: 20, step: 1 }, { description: 'Number of extra pixels at which to prefer atoms over bonds.' }),
    convertCoordsToRay: PD.Boolean(false, { description: 'Convert screen coordinates to ray for picking.' }),
};
export class Canvas3dInteractionHelper {
    setProps(props) {
        Object.assign(this.props, props);
    }
    getTarget() {
        if (this.ray) {
            return this.ray;
        }
        else if (this.props.convertCoordsToRay) {
            return this.camera.getRay(Ray3D(), this.endX, this.input.height - this.endY);
        }
        else {
            return Vec2.create(this.endX, this.endY);
        }
    }
    handleMove() {
        const xyChanged = this.startX !== this.endX || this.startY !== this.endY || (this.input.pointerLock && !this.controls.isMoving);
        if (xyChanged) {
            this.pickData = this.canvasAsyncIdentify(this.getTarget());
            this.startX = this.endX;
            this.startY = this.endY;
        }
    }
    handleClick() {
        const pickData = this.canvasIdentify(this.getTarget());
        const loci = this.getLoci(pickData === null || pickData === void 0 ? void 0 : pickData.id, pickData === null || pickData === void 0 ? void 0 : pickData.position);
        this.events.click.next({ current: loci, buttons: this.buttons, button: this.button, modifiers: this.modifiers, page: Vec2.create(this.endX, this.endY), position: pickData === null || pickData === void 0 ? void 0 : pickData.position });
        this.prevLoci = loci;
    }
    handleDrag() {
        const xyChanged = this.startX !== this.endX || this.startY !== this.endY || (this.input.pointerLock && !this.controls.isMoving);
        if (xyChanged && !this.outsideViewport(this.startX, this.startY, this.ray)) {
            this.events.drag.next({ current: this.prevLoci, buttons: this.buttons, button: this.button, modifiers: this.modifiers, pageStart: Vec2.create(this.startX, this.startY), pageEnd: Vec2.create(this.endX, this.endY) });
            this.startX = this.endX;
            this.startY = this.endY;
        }
    }
    tick(t) {
        if (!this.inside)
            return;
        if (this.pickData) {
            const pickData = this.pickData.tryGet();
            if (pickData !== 'pending') {
                this.position = pickData === null || pickData === void 0 ? void 0 : pickData.position;
                if (this.inside) {
                    const loci = this.getLoci(pickData === null || pickData === void 0 ? void 0 : pickData.id, pickData === null || pickData === void 0 ? void 0 : pickData.position);
                    this.events.hover.next({ current: loci, buttons: this.buttons, button: this.button, modifiers: this.modifiers, page: Vec2.create(this.endX, this.endY), position: pickData === null || pickData === void 0 ? void 0 : pickData.position });
                    this.prevLoci = loci;
                }
                this.pickData = undefined;
            }
        }
        if (t - this.prevT > 1000 / this.props.maxFps) {
            this.prevT = t;
            if (this.isInteracting) {
                this.handleDrag();
            }
            else {
                this.handleMove();
            }
        }
    }
    leave() {
        this.inside = false;
        if (!Representation.Loci.isEmpty(this.prevLoci)) {
            this.prevLoci = Representation.Loci.Empty;
            this.events.hover.next({ current: this.prevLoci, buttons: this.buttons, button: this.button, modifiers: this.modifiers });
        }
    }
    move(x, y, buttons, button, modifiers, ray) {
        this.inside = true;
        this.buttons = buttons;
        this.button = button;
        this.modifiers = modifiers;
        this.ray = ray;
        this.endX = x;
        this.endY = y;
    }
    click(x, y, buttons, button, modifiers, ray) {
        this.endX = x;
        this.endY = y;
        this.buttons = buttons;
        this.button = button;
        this.modifiers = modifiers;
        this.ray = ray;
        this.handleClick();
    }
    drag(x, y, buttons, button, modifiers) {
        this.endX = x;
        this.endY = y;
        this.buttons = buttons;
        this.button = button;
        this.modifiers = modifiers;
        this.handleDrag();
    }
    modify(modifiers) {
        if (ModifiersKeys.areEqual(modifiers, this.modifiers))
            return;
        this.modifiers = modifiers;
        this.events.hover.next({ current: this.prevLoci, buttons: this.buttons, button: this.button, modifiers: this.modifiers, page: Vec2.create(this.endX, this.endY), position: this.position });
    }
    outsideViewport(x, y, ray) {
        if (ray)
            return false;
        const { input, camera: { viewport } } = this;
        x *= input.pixelRatio;
        y *= input.pixelRatio;
        return (x > viewport.x + viewport.width ||
            input.height - y > viewport.y + viewport.height ||
            x < viewport.x ||
            input.height - y < viewport.y);
    }
    getLoci(pickingId, position) {
        var _a;
        const { repr, loci } = this.lociGetter(pickingId);
        if (position && repr && Bond.isLoci(loci) && loci.bonds.length === 2) {
            const { aUnit, aIndex } = loci.bonds[0];
            aUnit.conformation.position(aUnit.elements[aIndex], tmpPosA);
            Vec3.sub(tmpNorm, this.camera.state.position, this.camera.state.target);
            Vec3.projectPointOnPlane(tmpPos, position, tmpNorm, tmpPosA);
            const pixelSize = this.camera.getPixelSize(tmpPos);
            let radius = repr.theme.size.size(loci.bonds[0]) * ((_a = repr.props.sizeFactor) !== null && _a !== void 0 ? _a : 1);
            if (repr.props.lineSizeAttenuation === false) {
                // divide by two to get radius
                radius *= pixelSize / 2;
            }
            radius += this.props.preferAtomPixelPadding * pixelSize;
            if (Vec3.distance(tmpPos, tmpPosA) < radius) {
                return { repr, loci: Bond.toFirstStructureElementLoci(loci) };
            }
        }
        return { repr, loci };
    }
    dispose() {
        this.ev.dispose();
    }
    constructor(canvasIdentify, canvasAsyncIdentify, lociGetter, input, camera, controls, props = {}) {
        this.canvasIdentify = canvasIdentify;
        this.canvasAsyncIdentify = canvasAsyncIdentify;
        this.lociGetter = lociGetter;
        this.input = input;
        this.camera = camera;
        this.controls = controls;
        this.ev = RxEventHelper.create();
        this.events = {
            hover: this.ev(),
            drag: this.ev(),
            click: this.ev(),
        };
        this.startX = -1;
        this.startY = -1;
        this.endX = -1;
        this.endY = -1;
        this.ray = void 0;
        this.pickData = void 0;
        this.position = void 0;
        this.isInteracting = false;
        this.prevLoci = Representation.Loci.Empty;
        this.prevT = 0;
        this.inside = false;
        this.buttons = ButtonsType.create(0);
        this.button = ButtonsType.create(0);
        this.modifiers = ModifiersKeys.None;
        this.props = { ...PD.getDefaultValues(Canvas3dInteractionHelperParams), ...props };
        input.drag.subscribe(({ x, y, buttons, button, modifiers }) => {
            this.isInteracting = true;
            // console.log('drag');
            this.drag(x, y, buttons, button, modifiers);
        });
        input.move.subscribe(({ x, y, inside, buttons, button, modifiers, onElement, ray }) => {
            if (!inside || this.isInteracting)
                return;
            if (!onElement) {
                this.leave();
                return;
            }
            // console.log('move');
            this.move(x, y, buttons, button, modifiers, ray);
        });
        input.leave.subscribe(() => {
            // console.log('leave');
            this.leave();
        });
        input.click.subscribe(({ x, y, buttons, button, modifiers, ray }) => {
            if (this.outsideViewport(x, y, ray))
                return;
            // console.log('click');
            this.click(x, y, buttons, button, modifiers, ray);
        });
        input.interactionEnd.subscribe(() => {
            // console.log('interactionEnd');
            this.isInteracting = false;
        });
        input.modifiers.subscribe(modifiers => {
            // console.log('modifiers');
            this.modify(modifiers);
        });
    }
}
